import { INTERFACE_IDS } from '@lukso/lsp-smart-contracts'
import {
  INTERFACE_ID_LSP7_PREVIOUS,
  INTERFACE_ID_LSP7,
} from '@lukso/lsp7-contracts'
import {
  INTERFACE_ID_LSP8_PREVIOUS,
  INTERFACE_ID_LSP8,
} from '@lukso/lsp8-contracts'

import {
  DefaultTxComponents,
  InternalTransactionComponent,
  SET_DATA_BATCH_COMPONENT_NAME,
  SET_DATA_COMPONENT_NAME,
} from '@/shared/models/Transaction'
import {
  // LSP0
  SETDATA_ERC725Y,
  SETDATA_BATCH_ERC725Y,
  EXECUTE_BATCH_ERC725X,
  DEPLOY,
  TRANSFER_OWNERSHIP_LSP0,
  ACCEPT_OWNERSHIP_LSP0,
  RENOUNCE_OWNERSHIP_LSP0,

  // LSP7
  MINT_LSP7,
  TRANSFER_LSP7,
  AUTHORIZE_OPERATOR_LSP7,
  REVOKE_OPERATOR_LSP7_V12,
  REVOKE_OPERATOR_LSP7_V15,

  // LSP8
  MINT_LSP8,
  TRANSFER_LSP8,
  AUTHORIZE_OPERATOR_LSP8,
  REVOKE_OPERATOR_LSP8,

  // LSP26
  FOLLOW_LSP26,
  UNFOLLOW_LSP26,

  // ERC20
  MINT_ERC20,
  BURN_ERC20,
  BURN_FROM_ERC20,
  TRANSFER_ERC20,
  APPROVE_ERC20,

  // ERC721
  MINT_ERC721,
  SAFE_MINT_ERC721,
  BURN_ERC721,
  TRANSFER_FROM_ERC721,
  APPROVE_ERC721,

  // ERC777
  SEND_ERC777,
  BURN_ERC777,
  OPERATOR_SEND_ERC777,
  OPERATOR_BURN_ERC777,
  AUTHORIZE_OPERATOR_ERC777,

  // ERC1155
  MINT_ERC1155,
  SAFE_TRANSFER_FROM_ERC1155,
  MINT_BURNTPIX,
} from '@/transactionConfirmationSelectors/functionSignatures'

const SUPPORTS_INTERFACE_SIGNATURE = 'supportsInterface(bytes4)'
const DECIMALS_SIGNATURE = 'decimals()'

// The selector field should not be specified here.
// They will be calculated the first time the component is used.
// This is on purpose to avoid them from ever being out of sync
// with the functionSignature.
const componentSelectors: InternalTransactionComponent[] = [
  // Setting Data (ERC725Y)
  {
    functionSignature: SETDATA_ERC725Y,
    standard: 'ERC725Y',
    component: SET_DATA_COMPONENT_NAME,
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC725Y,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: SETDATA_BATCH_ERC725Y,
    standard: 'ERC725Y',
    component: SET_DATA_BATCH_COMPONENT_NAME,
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC725Y,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  // Generic Execution + Contract Deployment (ERC725X)
  {
    functionSignature: EXECUTE_BATCH_ERC725X,
    standard: 'ERC725X',
    component: DefaultTxComponents.EXECUTION,
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC725X,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: DEPLOY,
    component: 'ContractCreate',
  },
  // Non-Fungible Tokens Transfer
  //  - LSP7 that are non-divisible (0 decimals) + LSP4 Token Type as NFT (1) (TODO)
  //  - LSP8 (NFT - 1 or COLLECTION - 2)
  // THIS MUST COME FIRST, because it's more specific and checks for
  // decimals() 0 so the rest can do the same check without
  // the decimals() check to not require the NOT condition.
  ...[
    ...Object.values(INTERFACE_ID_LSP7_PREVIOUS),
    INTERFACE_ID_LSP7,
  ].map<InternalTransactionComponent>(lsp7InterfaceId => ({
    functionSignature: TRANSFER_LSP7,
    standard: 'LSP7',
    component: 'LSP8Transfer',
    conditions: [
      {
        type: 'call',
        functionSignature: DECIMALS_SIGNATURE,
        outputs: [
          {
            value: '0',
            type: 'uint8',
          },
        ],
      },
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp7InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  // We dont need to check decimal for LSP8, because it's always 0
  ...[
    ...Object.values(INTERFACE_ID_LSP8_PREVIOUS),
    INTERFACE_ID_LSP8,
  ].map<InternalTransactionComponent>(lsp8InterfaceId => ({
    functionSignature: TRANSFER_LSP8,
    standard: 'LSP8',
    component: 'LSP8Transfer',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp8InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  // Less specific decimals() != 0
  // Tokens Transfer
  ...[
    ...Object.values(INTERFACE_ID_LSP7_PREVIOUS),
    INTERFACE_ID_LSP7,
  ].map<InternalTransactionComponent>(lsp7InterfaceId => ({
    functionSignature: TRANSFER_LSP7,
    standard: 'LSP7',
    component: 'LSP7Transfer',
    conditions: [
      // Adds the isNegation to only check for LSP7 with decimals() != 0 as the above block already checks for LSP7 with decimals() == 0
      {
        type: 'call',
        functionSignature: DECIMALS_SIGNATURE,
        outputs: [
          {
            value: '0',
            isNegation: true,
            type: 'uint8',
          },
        ],
      },
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp7InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  // LSP0 Ownership functions
  {
    functionSignature: TRANSFER_OWNERSHIP_LSP0,
    standard: 'LSP0',
    component: 'TransferOwnership',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.LSP0ERC725Account,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: ACCEPT_OWNERSHIP_LSP0,
    component: 'TransferOwnership',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.LSP0ERC725Account,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: RENOUNCE_OWNERSHIP_LSP0,
    component: 'TransferOwnership',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.LSP0ERC725Account,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  // Operator approvals functionalities
  ...[
    ...Object.values(INTERFACE_ID_LSP7_PREVIOUS),
    INTERFACE_ID_LSP7,
  ].map<InternalTransactionComponent>(lsp7InterfaceId => ({
    functionSignature: AUTHORIZE_OPERATOR_LSP7,
    standard: 'LSP7',
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp7InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  ...[
    ...Object.values(INTERFACE_ID_LSP8_PREVIOUS),
    INTERFACE_ID_LSP8,
  ].map<InternalTransactionComponent>(lsp8InterfaceId => ({
    functionSignature: AUTHORIZE_OPERATOR_LSP8,
    standard: 'LSP8',
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp8InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  ...[
    ...Object.values(INTERFACE_ID_LSP8_PREVIOUS),
    INTERFACE_ID_LSP8,
  ].map<InternalTransactionComponent>(lsp8InterfaceId => ({
    functionSignature: REVOKE_OPERATOR_LSP8,
    standard: 'LSP8',
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp8InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  ...Object.values(
    INTERFACE_ID_LSP7_PREVIOUS
  ).map<InternalTransactionComponent>(lsp7InterfaceId => ({
    functionSignature: REVOKE_OPERATOR_LSP7_V12,
    standard: 'LSP7',
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp7InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  {
    functionSignature: REVOKE_OPERATOR_LSP7_V15,
    standard: 'LSP7',
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_ID_LSP7,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  // Legacy Token Transfer functionalities (ERC20, ERC721, ERC777, ERC1155)
  {
    functionSignature: TRANSFER_ERC20,
    standard: 'ERC20',
    legacy: true,
    component: 'ERC20ERC777Transfer',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC20,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: TRANSFER_FROM_ERC721,
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC721,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
    standard: 'ERC721',
    legacy: true,
    component: 'ERC721TransferFrom',
  },
  {
    functionSignature: SEND_ERC777,
    standard: 'ERC777',
    legacy: true,
    component: 'ERC20ERC777Transfer',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC777,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: OPERATOR_SEND_ERC777,
    standard: 'ERC777',
    legacy: true,
    component: 'OperatorSend',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC777,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: SAFE_TRANSFER_FROM_ERC1155,
    standard: 'ERC1155',
    legacy: true,
    component: 'SafeTransferFrom',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC1155,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  // Legacy Token Approval functionalities (ERC20, ERC721, ...)
  {
    functionSignature: APPROVE_ERC20,
    standard: 'ERC20',
    legacy: true,
    component: 'Approve',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC20,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: APPROVE_ERC721,
    standard: 'ERC721',
    legacy: true,
    component: 'Approve',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC721,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: AUTHORIZE_OPERATOR_ERC777,
    standard: 'ERC777',
    legacy: true,
    component: 'AuthorizeOperator',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC777,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  // Non-Standard public `mint(...)` and `burn(...)` functions
  ...[
    ...Object.values(INTERFACE_ID_LSP7_PREVIOUS),
    INTERFACE_ID_LSP7,
  ].map<InternalTransactionComponent>(lsp7InterfaceId => ({
    functionSignature: MINT_LSP7,
    standard: 'LSP7Mintable', // non-standard function
    component: 'LSP7Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp7InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  ...[
    ...Object.values(INTERFACE_ID_LSP8_PREVIOUS),
    INTERFACE_ID_LSP8,
  ].map<InternalTransactionComponent>(lsp8InterfaceId => ({
    functionSignature: MINT_BURNTPIX,
    standard: 'LSP8Mintable', // non-standard function
    component: 'LSP8Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp8InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  {
    functionSignature: MINT_ERC20,
    standard: 'ERC20', // non-standard function
    component: 'ERC20And721Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC20,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: MINT_ERC721,
    standard: 'ERC721', // non-standard function
    component: 'ERC20And721Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC721,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: SAFE_MINT_ERC721,
    standard: 'ERC721', // non-standard function
    component: 'ERC20And721Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC721,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: MINT_ERC1155,
    standard: 'ERC1155Mint', // non-standard function
    component: 'ERC1155Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC1155,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  ...[
    ...Object.values(INTERFACE_ID_LSP8_PREVIOUS),
    INTERFACE_ID_LSP8,
  ].map<InternalTransactionComponent>(lsp8InterfaceId => ({
    functionSignature: MINT_LSP8,
    standard: 'LSP8Mintable', // non-standard function
    component: 'LSP8Mint',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: lsp8InterfaceId,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  })),
  {
    functionSignature: BURN_ERC777,
    standard: 'ERC777',
    legacy: true,
    component: 'Burn',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC777,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: BURN_FROM_ERC20,
    standard: 'ERC20',
    component: 'Burn',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC20,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: BURN_ERC20,
    standard: 'ERC20',
    legacy: true,
    component: 'Burn',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC20,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: BURN_ERC721,
    standard: 'ERC721',
    legacy: true,
    component: 'Burn',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC721,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: OPERATOR_BURN_ERC777,
    standard: 'ERC777',
    legacy: true,
    component: 'OperatorBurn',
    conditions: [
      {
        type: 'call',
        functionSignature: SUPPORTS_INTERFACE_SIGNATURE,
        inputs: [
          {
            value: INTERFACE_IDS.ERC777,
            type: 'bytes4',
          },
        ],
        outputs: [
          {
            value: true,
            type: 'bool',
          },
        ],
      },
    ],
  },
  {
    functionSignature: FOLLOW_LSP26,
    standard: 'LSP26',
    component: 'LSP26FollowerSystem',
    conditions: [
      // TODO add check if contract address matches
    ],
  },
  {
    functionSignature: UNFOLLOW_LSP26,
    standard: 'LSP26',
    component: 'LSP26FollowerSystem',
    conditions: [
      // TODO add check if contract address matches
    ],
  },
]

export default componentSelectors
